import React, { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { yupResolver } from '@hookform/resolvers/yup';
import { Avatar, Box, Button, Grid, Typography } from '@mui/joy';

import { SelectField } from '../../components/FormElements/SelectField';
import { TextInputField } from '../../components/FormElements/TextInputField';
import { UserProfileCard } from '../../components/UserProfileCard';
import { checkAndSetLanguage } from '../../general-utilities/languageUtils';
import getEditDetailsSchema from '../../schema/editDetailsSchema';
import { LanguageService } from '../../services/LanguageService';
import { User, UserPreferredLanguage, UserUpdateInput } from '../../services/types/api.generated';
import { UserService } from '../../services/UserService';
import sessionStore from '../../stores/SessionStore';

interface UserDetailsFormProps {
  userData: User | null;
  isEditing: boolean;
  handleEditDetails: () => void;
  handleCancelEdit: () => void;
}

interface UserDetailsFormData {
  fullName: string;
  email: string;
  preferred_language: string;
}

export const UserDetailsForm: React.FC<UserDetailsFormProps> = ({
  userData,
  isEditing,
  handleEditDetails,
  handleCancelEdit
}) => {
  const { t } = useTranslation();
  const yupSchema = getEditDetailsSchema(t);
  const [languageOptions, setLanguageOptions] = useState<{ value: string; label: string }[]>([]);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues: {
      fullName: '',
      email: '',
      preferred_language: ''
    },
    resolver: yupResolver(yupSchema)
  });

  useEffect(() => {
    reset({
      fullName: `${userData?.first_name || ''} ${userData?.last_name || ''}`,
      email: userData?.email || '',
      preferred_language: userData?.preferred_language || 'en'
    });
  }, [userData, reset]);

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const languageData = await LanguageService.getLanguages();
        const mappedLanguages = languageData.map((lang) => ({
          value: lang.code,
          label: lang.name
        }));
        setLanguageOptions(mappedLanguages);
      } catch (error) {
        console.error('Error fetching languages:', error);
        toast.error(t('edit_profile.errors.fetch_languages'));
      }
    };

    fetchLanguages();
  }, []);

  const onSubmit: SubmitHandler<UserDetailsFormData> = async (data: UserDetailsFormData) => {
    try {
      const [firstName, lastName] = data.fullName.split(' ');

      if (!userData) {
        return;
      }

      const updatedUser: UserUpdateInput = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: data.email.trim(),
        preferred_language: (data.preferred_language || 'en') as UserPreferredLanguage
      };

      const response = await UserService.updateUser(userData.id, updatedUser);
      if (response) {
        sessionStore.setUser(response);
      }
      handleCancelEdit();
      checkAndSetLanguage();
      toast.success(t('edit_profile.account_details_updated'));
    } catch (error) {
      toast.error(`${t('edit_profile.errors.account_details_update')}: ${error}`);
    }
  };

  const getAvatar = (ud: User | null) => {
    if (!ud) {
      return null;
    }
    if (ud.avatar) {
      return <Avatar src={ud.avatar} sx={{ width: 140, height: 140 }} />;
    }
    return <Avatar>{ud?.initials}</Avatar>;
  };

  return (
    <UserProfileCard>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        {isEditing ? (
          <Typography level='h3'>{t('edit_profile.your_details')}</Typography>
        ) : (
          <Typography level='h3'>{t('edit_profile.account_details')}</Typography>
        )}
        {!isEditing && (
          <Button color='neutral' onClick={handleEditDetails} variant='outlined'>
            {t('edit_profile.edit_details')}
          </Button>
        )}
      </Box>

      {isEditing ? (
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid xs={12}>{getAvatar(userData)}</Grid>

          {/* Full Name Field */}
          <Grid md={6} sx={{ py: 0 }} xs={12}>
            <Controller
              control={control}
              name='fullName'
              render={({ field }) => (
                <TextInputField
                  label={t('edit_profile.full_name')}
                  {...field}
                  error={errors.fullName ? errors.fullName.message : ''}
                />
              )}
            />
          </Grid>

          {/* Language Field */}
          <Grid md={6} sx={{ py: 0 }} xs={12}>
            <Controller
              control={control}
              name='preferred_language'
              render={({ field }) => (
                <SelectField
                  label={t('edit_profile.language')}
                  {...field}
                  error={errors.preferred_language ? errors.preferred_language.message : ''}
                  options={languageOptions}
                />
              )}
            />
          </Grid>

          {/* Email Field */}
          <Grid md={6} sx={{ py: 0 }} xs={12}>
            <Controller
              control={control}
              name='email'
              render={({ field }) => (
                <TextInputField
                  label={t('edit_profile.email')}
                  {...field}
                  error={errors.email ? errors.email.message : ''}
                />
              )}
            />
          </Grid>

          <Grid sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2 }} xs={12}>
            <Button color='neutral' disabled={isSubmitting} onClick={handleSubmit(onSubmit)}>
              {isSubmitting ? t('edit_profile.saving') : t('edit_profile.save_changes')}
            </Button>
            <Button color='neutral' onClick={handleCancelEdit} variant='outlined'>
              {t('edit_profile.cancel')}
            </Button>
          </Grid>
        </Grid>
      ) : (
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid md={3} xs={6}>
            <Typography>{t('edit_profile.avatar')}</Typography>
          </Grid>
          <Grid md={9} xs={6}>
            {getAvatar(userData)}
          </Grid>
          <Grid md={3} xs={6}>
            <Typography>{t('edit_profile.full_name')}</Typography>
          </Grid>
          <Grid md={9} xs={6}>
            <Typography>{`${userData?.first_name} ${userData?.last_name}`}</Typography>
          </Grid>
          <Grid md={3} xs={6}>
            <Typography>{t('edit_profile.language')}</Typography>
          </Grid>
          <Grid md={9} xs={6}>
            <Typography>
              {languageOptions.find((lang) => lang.value === userData?.preferred_language)?.label || 'English'}
            </Typography>
          </Grid>
          <Grid md={3} xs={6}>
            <Typography>{t('edit_profile.email')}</Typography>
          </Grid>
          <Grid md={9} xs={6}>
            <Typography>{userData?.email}</Typography>
          </Grid>
        </Grid>
      )}
    </UserProfileCard>
  );
};
